import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import {
  DollarSign, TrendingUp, Clock, RefreshCw, Download, Search,
  CheckCircle, AlertCircle, XCircle, FileText, BarChart3, Calendar,
  CreditCard, Building2, Users, ArrowUpRight, ArrowDownRight, ExternalLink,
  Wallet, PiggyBank, Landmark, ChevronRight, Loader2, Activity, Zap,
  TrendingDown, Percent, Globe, Shield, Ticket, Receipt
} from "lucide-react";
import { format, parseISO } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";

// Import extracted components
import { PromoCodesTab, InvoicesTab, CollectionsWorkspace } from "../components/financials";
import AdminOrgInvoices from "./AdminOrgInvoices";
import './SupportFinancialsAthletic.css';

const ORG_FINANCIAL_VIEWS = new Set([
  "transactions", "collections", "payouts", "registrations", "receivables", "discounts", "billing"
]);

const normalizeOrgFinancialView = (value, stripeActive) => {
  if (value === "promo-codes") return "discounts";
  if (value === "invoices") return "billing";
  if (ORG_FINANCIAL_VIEWS.has(value)) return value;
  return stripeActive ? "transactions" : "receivables";
};

// Simple bar chart component
const VolumeChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const maxVolume = Math.max(...data.map(d => d.volume), 1);
  
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((day, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
            style={{ height: `${(day.volume / maxVolume) * 100}%`, minHeight: day.volume > 0 ? '4px' : '0' }}
            title={`$${day.volume.toLocaleString()}`}
          />
          {idx % 2 === 0 && (
            <span className="text-[10px] text-slate-400">
              {format(parseISO(day.date), "M/d")}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const EmbeddedLayout = ({ children }) => <>{children}</>;

const FinanceHeader = (props) => <EditorialPageHeader
  eyebrow="Finance"
  title="Financials"
  description="Track revenue, payouts, refunds, and payment exceptions from registration to bank."
  headingId="financials-heading"
  {...props}
/>;

const AdminFinancials = ({ embedded = false, peopleRouteBase = "/admin" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);
  
  // Platform data (super admin)
  const [platformData, setPlatformData] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountTransactions, setAccountTransactions] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  
  // Org level data
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    total_revenue: 0,
    pending_amount: 0,
    refunded_amount: 0,
    paid_count: 0,
    pending_count: 0,
    refunded_count: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Stripe Connect data for org admins
  const [stripeData, setStripeData] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";

  const handleOrgViewChange = (value) => {
    setActiveTab(value);
    setSearchParams({ view: value }, { replace: true });
  };

  useEffect(() => {
    if (!isSuperAdmin && !loading) {
      setActiveTab(normalizeOrgFinancialView(searchParams.get("view"), stripeData?.connected && stripeData?.active));
    }
  }, [isSuperAdmin, loading, searchParams, stripeData?.connected, stripeData?.active]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPlatformData();
    } else {
      fetchOrgFinancials();
    }
  }, [token, isSuperAdmin]);

  const fetchPlatformData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dashboardRes, accountsRes] = await Promise.all([
        axios.get(`${API}/admin/stripe/platform-dashboard`, { headers }),
        axios.get(`${API}/admin/stripe/connected-accounts`, { headers })
      ]);
      setPlatformData(dashboardRes.data);
      setConnectedAccounts(accountsRes.data.accounts);
    } catch (error) {
      console.error("Error fetching platform data:", error);
      setLoadError(error.response?.data?.detail || "We couldn't load the platform financial workspace. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgFinancials = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch both internal payments AND Stripe data
      const [paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/payments/payments/history`, { headers }),
        axios.get(`${API}/admin/financial-stats`, { headers })
      ]);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
      
      // Fetch Stripe Connect data if org has it
      if (user?.org_id) {
        setStripeLoading(true);
        try {
          const stripeRes = await axios.get(`${API}/organizations/${user.org_id}/stripe-financials`, { headers });
          setStripeData(stripeRes.data);
        } catch (stripeError) {
          console.log("Stripe data not available:", stripeError.response?.data?.message || stripeError.message);
          setStripeData(null);
        } finally {
          setStripeLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching financials:", error);
      setLoadError(error.response?.data?.detail || "We couldn't load your organization's financial activity. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (isSuperAdmin) {
      await fetchPlatformData();
    } else {
      await fetchOrgFinancials();
    }
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleConnectStripe = async () => {
    if (!user?.org_id || connectingStripe) return;
    setConnectingStripe(true);
    try {
      const response = stripeData?.connected
        ? await axios.post(`${API}/organizations/${user.org_id}/stripe-connect/onboard`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : await axios.post(`${API}/organizations/${user.org_id}/stripe-connect`, {
            return_url: window.location.href,
          }, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.data?.url) throw new Error("Stripe onboarding URL was not returned");
      window.location.assign(response.data.url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open Stripe setup");
      setConnectingStripe(false);
    }
  };

  // State for account details (fetched on demand)
  const [accountDetails, setAccountDetails] = useState(null);
  const [loadingAccountDetails, setLoadingAccountDetails] = useState(false);

  const handleViewAccountDetails = async (account) => {
    setSelectedAccount(account);
    setAccountDetails(null);
    setAccountTransactions(null);
    setAccountDialogOpen(true);
    setLoadingAccountDetails(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch both details and transactions in parallel
      const [detailsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/stripe/account/${account.account_id}/details`, { headers }),
        axios.get(`${API}/admin/stripe/account/${account.account_id}/transactions`, { headers })
      ]);
      setAccountDetails(detailsRes.data);
      setAccountTransactions(transactionsRes.data);
    } catch (error) {
      console.error("Error fetching account details:", error);
      toast.error("Failed to load account details");
    } finally {
      setLoadingAccountDetails(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = searchTerm === "" ||
      payment.player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.program_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || payment.payment_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const exportToCSV = () => {
    const headers = ["Date", "Player", "Program", "Amount", "Status", "Method"];
    const rows = filteredPayments.map(p => [
      p.created_at ? format(new Date(p.created_at), "yyyy-MM-dd") : "",
      p.player_name || "",
      p.program_name || "",
      p.amount || 0,
      p.payment_status || "",
      p.payment_method || ""
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    toast.success("Report exported");
    trackAdminWorkflowCompleted("payments");
  };

  if (loading) {
    return (
      <PageLayout title="Financials" subtitle="Track revenue, payouts, refunds, and payment exceptions." headerEyebrow="Finance">
        {embedded && <FinanceHeader />}
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading financials">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
        </div>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout title="Financials" subtitle="Track revenue, payouts, refunds, and payment exceptions." headerEyebrow="Finance">
        {embedded && <FinanceHeader />}
        <section className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
          <h2 className="text-lg font-semibold text-rose-950">Financials unavailable</h2>
          <p className="mt-2 text-sm leading-6 text-rose-800">{loadError}</p>
          <Button onClick={isSuperAdmin ? fetchPlatformData : fetchOrgFinancials} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
        </section>
      </PageLayout>
    );
  }

  // Super Admin Stripe Dashboard
  if (isSuperAdmin && platformData) {
    return (
      <PageLayout>
        <div className="space-y-6 operations-page financial-operations" data-testid="super-admin-financials">
          <FinanceHeader description="Monitor platform money movement, connected accounts, and payout readiness." />
          {/* Header */}
          <div className="operations-actions flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">Platform-wide financial overview</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="rounded-xl"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
              <a 
                href="https://dashboard.stripe.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm" className="rounded-xl">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Stripe Dashboard
                </Button>
              </a>
            </div>
          </div>

          {/* Balance Cards */}
          <div className="finance-metric-grid grid grid-cols-1 md:grid-cols-4">
            <div className="finance-metric-primary p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Available Balance</p>
                  <p className="text-3xl font-bold mt-1">
                    ${platformData.balance.available.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6" />
                </div>
              </div>
              <p className="text-emerald-100 text-xs mt-3">
                Ready for payout
              </p>
            </div>

            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Pending Balance</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    ${platformData.balance.pending.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <p className="text-slate-400 text-xs mt-3">
                Awaiting clearance
              </p>
            </div>

            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Platform Fees (30d)</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    ${platformData.volume.platform_fees_30d.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
                  <Percent className="w-6 h-6 text-violet-600" />
                </div>
              </div>
              <p className="text-slate-400 text-xs mt-3">
                Your revenue share
              </p>
            </div>

            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Volume (30d)</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    ${platformData.volume.total_30d.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-slate-400 text-xs mt-3">
                {platformData.volume.successful_charges} transactions
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="finance-tabs bg-slate-100 p-1">
              <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="accounts" className="rounded-xl data-[state=active]:bg-white">
                <Building2 className="w-4 h-4 mr-2" />
                Connected Accounts
              </TabsTrigger>
              <TabsTrigger value="transactions" className="rounded-xl data-[state=active]:bg-white">
                <FileText className="w-4 h-4 mr-2" />
                Transactions
              </TabsTrigger>
              <TabsTrigger value="payouts" className="rounded-xl data-[state=active]:bg-white">
                <Landmark className="w-4 h-4 mr-2" />
                Payouts
              </TabsTrigger>
              <TabsTrigger value="promo-codes" className="rounded-xl data-[state=active]:bg-white">
                <Ticket className="w-4 h-4 mr-2" />
                Promo Codes
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Volume Chart */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Daily Volume (14 days)</h3>
                  <VolumeChart data={platformData.daily_volume} />
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-500">Total Volume</p>
                      <p className="text-lg font-semibold text-slate-900">
                        ${platformData.daily_volume.reduce((sum, d) => sum + d.volume, 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Transactions</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {platformData.daily_volume.reduce((sum, d) => sum + d.count, 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Platform Fees</p>
                      <p className="text-lg font-semibold text-emerald-600">
                        ${platformData.daily_volume.reduce((sum, d) => sum + d.fees, 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="space-y-4">
                  {/* Success Rate */}
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Payment Success Rate</p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">
                          {platformData.volume.success_rate}%
                        </p>
                      </div>
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                        platformData.volume.success_rate >= 95 ? 'bg-emerald-50' : 'bg-amber-50'
                      }`}>
                        {platformData.volume.success_rate >= 95 ? (
                          <CheckCircle className="w-7 h-7 text-emerald-600" />
                        ) : (
                          <AlertCircle className="w-7 h-7 text-amber-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {platformData.volume.successful_charges} successful
                      </span>
                      <span className="text-red-500 flex items-center gap-1">
                        <XCircle className="w-4 h-4" />
                        {platformData.volume.failed_charges} failed
                      </span>
                    </div>
                  </div>

                  {/* Connected Accounts Summary */}
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Connected Organizations</p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">
                          {platformData.connected_accounts.total}
                        </p>
                      </div>
                      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-emerald-600 flex items-center gap-1">
                        <Zap className="w-4 h-4" />
                        {platformData.connected_accounts.active} active
                      </span>
                      <span className="text-amber-600 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {platformData.connected_accounts.pending} pending
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Connected Accounts Tab */}
            <TabsContent value="accounts" className="mt-6">
              <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Connected Stripe Accounts</h3>
                  <Badge variant="outline" className="rounded-lg">
                    {connectedAccounts.length} accounts
                  </Badge>
                </div>
                <div className="divide-y divide-slate-100">
                  {connectedAccounts.length === 0 ? (
                    <div className="p-8 text-center">
                      <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No connected accounts yet</p>
                    </div>
                  ) : (
                    connectedAccounts.map(account => (
                      <div 
                        key={account.account_id}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => handleViewAccountDetails(account)}
                        data-testid={`account-row-${account.account_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              account.charges_enabled ? 'bg-emerald-100' : 'bg-amber-100'
                            }`}>
                              <Building2 className={`w-5 h-5 ${
                                account.charges_enabled ? 'text-emerald-600' : 'text-amber-600'
                              }`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{account.org_name}</p>
                                <Badge variant="outline" className={`rounded-lg text-xs capitalize ${
                                  account.subscription_tier === 'premium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  account.subscription_tier === 'plus' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  'bg-slate-50 text-slate-600'
                                }`}>
                                  {account.subscription_tier}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500">{account.email || 'No email'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-slate-500">{account.country}</p>
                              <p className="text-xs text-slate-400">
                                {account.created ? format(parseISO(account.created), "MMM d, yyyy") : ''}
                              </p>
                            </div>
                            <Badge className={`rounded-lg ${
                              account.charges_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {account.charges_enabled ? 'Active' : 'Pending'}
                            </Badge>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="mt-6">
              <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Recent Transactions</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {platformData.recent_transactions.length === 0 ? (
                    <div className="p-8 text-center">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No transactions yet</p>
                    </div>
                  ) : (
                    platformData.recent_transactions.map(txn => (
                      <div key={txn.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            txn.status === 'succeeded' ? 'bg-emerald-100' : 
                            txn.status === 'pending' ? 'bg-amber-100' : 'bg-red-100'
                          }`}>
                            {txn.status === 'succeeded' ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            ) : txn.status === 'pending' ? (
                              <Clock className="w-5 h-5 text-amber-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{txn.description}</p>
                            <p className="text-sm text-slate-500">
                              {txn.customer_email || 'No email'} • {format(parseISO(txn.created), "MMM d, h:mm a")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">${txn.amount.toLocaleString()}</p>
                            {txn.fee > 0 && (
                              <p className="text-xs text-emerald-600">+${txn.fee.toFixed(2)} fee</p>
                            )}
                          </div>
                          <Badge className={`rounded-lg capitalize ${
                            txn.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' :
                            txn.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {txn.status}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts" className="mt-6">
              <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Recent Payouts</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {platformData.recent_payouts.length === 0 ? (
                    <div className="p-8 text-center">
                      <Landmark className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No payouts yet</p>
                    </div>
                  ) : (
                    platformData.recent_payouts.map(payout => (
                      <div key={payout.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            payout.status === 'paid' ? 'bg-emerald-100' : 
                            payout.status === 'pending' || payout.status === 'in_transit' ? 'bg-blue-100' : 'bg-red-100'
                          }`}>
                            <Landmark className={`w-5 h-5 ${
                              payout.status === 'paid' ? 'text-emerald-600' : 
                              payout.status === 'pending' || payout.status === 'in_transit' ? 'text-blue-600' : 'text-red-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              ${payout.amount.toLocaleString()} Payout
                            </p>
                            <p className="text-sm text-slate-500">
                              {payout.arrival_date ? `Arrives ${format(parseISO(payout.arrival_date), "MMM d")}` : 'Processing'}
                            </p>
                          </div>
                        </div>
                        <Badge className={`rounded-lg capitalize ${
                          payout.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          payout.status === 'pending' || payout.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {payout.status === 'in_transit' ? 'In Transit' : payout.status}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Promo Codes Tab */}
            <TabsContent value="promo-codes" className="mt-6">
              <PromoCodesTab token={token} isSuperAdmin={true} />
            </TabsContent>
          </Tabs>

          {/* Account Details Dialog */}
          <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
            <DialogContent className="max-w-2xl rounded-2xl">
              <DialogHeader>
                <DialogTitle>{selectedAccount?.org_name}</DialogTitle>
                <DialogDescription>
                  Connected Stripe Account Details
                </DialogDescription>
              </DialogHeader>
              
              {selectedAccount && (
                <div className="space-y-4">
                  {/* Account Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Account ID</p>
                      <p className="font-mono text-sm text-slate-900">{selectedAccount.account_id}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Status</p>
                      <Badge className={`mt-1 rounded-lg ${
                        selectedAccount.charges_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {selectedAccount.charges_enabled ? 'Active' : 'Pending Setup'}
                      </Badge>
                    </div>
                  </div>

                  {/* Stats - Show loading or data */}
                  {loadingAccountDetails ? (
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="bg-slate-50 rounded-xl p-4 animate-pulse">
                          <div className="h-3 w-20 bg-slate-200 rounded mb-2"></div>
                          <div className="h-6 w-16 bg-slate-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : accountDetails ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-emerald-50 rounded-xl p-4">
                        <p className="text-xs text-emerald-600">30-Day Volume</p>
                        <p className="text-xl font-bold text-emerald-700">
                          ${accountDetails.volume_30d?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-emerald-500 mt-1">
                          {accountDetails.charge_count_30d || 0} transactions
                        </p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-xs text-blue-600">Available Balance</p>
                        <p className="text-xl font-bold text-blue-700">
                          ${accountDetails.balance_available?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4">
                        <p className="text-xs text-amber-600">Pending Balance</p>
                        <p className="text-xl font-bold text-amber-700">
                          ${accountDetails.balance_pending?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>
                  ) : null}

                  {/* Recent Transactions */}
                  {loadingAccountDetails ? (
                    <div className="animate-pulse">
                      <div className="h-4 w-32 bg-slate-200 rounded mb-3"></div>
                      <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-10 bg-slate-200 rounded"></div>
                        ))}
                      </div>
                    </div>
                  ) : accountTransactions ? (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Recent Transactions</h4>
                      <div className="bg-slate-50 rounded-xl divide-y divide-slate-200 max-h-60 overflow-y-auto">
                        {accountTransactions.transactions.length === 0 ? (
                          <p className="p-4 text-center text-slate-500 text-sm">No transactions</p>
                        ) : (
                          accountTransactions.transactions.slice(0, 10).map(txn => (
                            <div key={txn.id} className="p-3 flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-slate-900">{txn.description}</p>
                                <p className="text-xs text-slate-500">
                                  {format(parseISO(txn.created), "MMM d, h:mm a")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">
                                  ${txn.amount.toLocaleString()}
                                </span>
                                <Badge className={`text-xs rounded ${
                                  txn.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'
                                }`}>
                                  {txn.status}
                                </Badge>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </PageLayout>
    );
  }

  // Org Admin Financials with Stripe Connect integration
  return (
    <PageLayout>
      <div className="finance-athletic space-y-6 operations-page financial-operations" data-testid="org-financials">
        <FinanceHeader />
        <section className="flex flex-col gap-3 border-b border-[#dfe6df] pb-4 sm:flex-row sm:items-center sm:justify-between" aria-label="Financial dashboard controls">
          <div className="flex items-center gap-3">
            <span className={`inline-flex h-2.5 w-2.5 rounded-full ${stripeData?.active ? "bg-[#19a974]" : "bg-[#d6902f]"}`} aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-[#26312f]">{stripeData?.active ? "Payments active" : "Payments need attention"}</p>
              <p className="text-xs text-[#60706a]">{stripeData?.active ? "Stripe is connected and ready to collect payments." : "Complete Stripe setup to accept online payments."}</p>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="rounded-xl border-[#cbd3cd] bg-white text-[#26312f] hover:bg-[#f3f7f2] hover:text-[#101a19]"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
              {stripeData?.connected && stripeData?.active && (
                <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-xl border-[#cbd3cd] bg-white text-[#26312f] hover:bg-[#f3f7f2] hover:text-[#101a19]">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Stripe Dashboard
                  </Button>
                </a>
              )}
          </div>
        </section>

        {/* Stripe Connect Banner (if not connected or incomplete) */}
        {stripeData && !stripeData.active && (
          <div className="finance-setup border rounded p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">
                  {stripeData.connected ? "Complete Stripe Setup" : "Connect Stripe to Accept Payments"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {stripeData.connected 
                    ? "Your Stripe account setup is incomplete. Complete onboarding to start accepting payments and view your financials."
                    : "Connect your Stripe account to accept online payments, view transaction history, and manage payouts."}
                </p>
                <Button onClick={handleConnectStripe} disabled={connectingStripe} className="finance-primary mt-4 rounded" size="sm">
                  {connectingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  {stripeData.connected ? "Complete Setup" : "Connect Stripe"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Balance Cards - Only show if connected and active */}
        {stripeData?.connected && stripeData?.active && (
          <>
            <section aria-labelledby="financial-snapshot-heading" className="space-y-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#087f67]">At a glance</p>
                  <h2 id="financial-snapshot-heading" className="text-xl font-semibold tracking-tight text-[#101a19]">Financial snapshot</h2>
                </div>
                <p className="text-sm text-[#60706a]">Balances and activity from the last 30 days</p>
              </div>
              <div className="finance-metric-grid grid grid-cols-1 md:grid-cols-4">
              <div className="finance-metric-primary p-5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Available Balance</p>
                    <p className="text-3xl font-bold mt-1">
                      ${stripeData.balance?.available?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Wallet className="w-6 h-6" />
                  </div>
                </div>
                <p className="text-emerald-100 text-xs mt-3">
                  Ready for payout • {stripeData.balance?.currency || 'USD'}
                </p>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Pending Balance</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      ${stripeData.balance?.pending?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <p className="text-slate-400 text-xs mt-3">
                  Awaiting clearance
                </p>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">30-Day Volume</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      ${stripeData.period_stats?.total_volume?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-slate-400 text-xs mt-3">
                  {stripeData.period_stats?.successful_charges || 0} successful charges
                </p>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Net Earnings</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      ${stripeData.period_stats?.net_volume?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <PiggyBank className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-slate-400 text-xs mt-3">
                  After ${stripeData.period_stats?.total_fees?.toFixed(2) || '0'} in fees
                </p>
              </div>
              </div>
            </section>

            <section className="financial-action-queue" aria-labelledby="financial-action-heading">
              <div>
                <p className="platform-eyebrow">Action required</p>
                <h2 id="financial-action-heading">Keep collections moving.</h2>
              </div>
              {stats.pending_count > 0 ? (
                <button type="button" onClick={() => handleOrgViewChange("collections")}>
                  <Clock className="w-5 h-5" />
                  <span><strong>{stats.pending_count} awaiting payment</strong><small>${stats.pending_amount?.toLocaleString() || 0} needs follow-up</small></span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="financial-action-clear"><CheckCircle className="w-5 h-5" /> No payment exceptions need attention.</div>
              )}
            </section>

            <Tabs value={activeTab} onValueChange={handleOrgViewChange} className="w-full">
              <TabsList className="finance-tabs financial-workspace-tabs bg-slate-100 p-1">
                <TabsTrigger value="transactions" className="rounded-lg">
                  <Activity className="w-4 h-4 mr-2" />
                  Ledger
                </TabsTrigger>
                <TabsTrigger value="collections" className="rounded-lg">
                  <Clock className="w-4 h-4 mr-2" />
                  Collections
                </TabsTrigger>
                <TabsTrigger value="receivables" className="rounded-lg">
                  <FileText className="w-4 h-4 mr-2" />
                  Family Receivables
                </TabsTrigger>
                <TabsTrigger value="payouts" className="rounded-lg">
                  <Landmark className="w-4 h-4 mr-2" />
                  Payouts
                </TabsTrigger>
                <TabsTrigger value="registrations" className="rounded-lg">
                  <Users className="w-4 h-4 mr-2" />
                  Registrations
                </TabsTrigger>
                <TabsTrigger value="discounts" className="rounded-lg">
                  <Ticket className="w-4 h-4 mr-2" />
                  Discounts
                </TabsTrigger>
                <TabsTrigger value="billing" className="rounded-lg">
                  <Receipt className="w-4 h-4 mr-2" />
                  Sporthouse Billing
                </TabsTrigger>
              </TabsList>

              <TabsContent value="receivables" className="mt-6">
                <AdminOrgInvoices embedded />
              </TabsContent>

              <TabsContent value="collections" className="mt-6">
                <CollectionsWorkspace token={token} peopleRouteBase={peopleRouteBase} />
              </TabsContent>

              {/* Stripe Transactions Tab */}
              <TabsContent value="transactions" className="mt-6">
                <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Recent Transactions</h3>
                    <span className="text-sm text-slate-500">Last 30 days</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(!stripeData.recent_transactions || stripeData.recent_transactions.length === 0) ? (
                      <div className="p-12 text-center">
                        <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No transactions yet</p>
                        <p className="text-sm text-slate-400 mt-1">Transactions will appear here once you start accepting payments</p>
                      </div>
                    ) : (
                      stripeData.recent_transactions.map(txn => (
                        <div key={txn.id} className="finance-transaction-row p-4 flex items-center justify-between hover:bg-slate-50">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              txn.status === 'succeeded' ? 'bg-emerald-100' : 
                              txn.status === 'pending' ? 'bg-amber-100' : 'bg-red-100'
                            }`}>
                              <CreditCard className={`w-5 h-5 ${
                                txn.status === 'succeeded' ? 'text-emerald-600' : 
                                txn.status === 'pending' ? 'text-amber-600' : 'text-red-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{txn.description}</p>
                              <p className="text-sm text-slate-500">
                                {txn.customer_email || 'Customer'} • {format(new Date(txn.created * 1000), "MMM d, h:mm a")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">${txn.amount.toLocaleString()}</p>
                              {txn.fee > 0 && (
                                <p className="text-xs text-slate-400">-${txn.fee.toFixed(2)} fee</p>
                              )}
                            </div>
                            <Badge className={`rounded-lg capitalize ${
                              txn.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' :
                              txn.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {txn.refunded ? 'Refunded' : txn.status}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Payouts Tab */}
              <TabsContent value="payouts" className="mt-6">
                <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Recent Payouts</h3>
                    {stripeData.payout_schedule && (
                      <span className="text-sm text-slate-500">
                        {stripeData.payout_schedule.interval} payouts • {stripeData.payout_schedule.delay_days} day delay
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(!stripeData.recent_payouts || stripeData.recent_payouts.length === 0) ? (
                      <div className="p-12 text-center">
                        <Landmark className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No payouts yet</p>
                        <p className="text-sm text-slate-400 mt-1">Payouts will be sent to your bank account automatically</p>
                      </div>
                    ) : (
                      stripeData.recent_payouts.map(payout => (
                        <div key={payout.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              payout.status === 'paid' ? 'bg-emerald-100' : 
                              payout.status === 'pending' || payout.status === 'in_transit' ? 'bg-blue-100' : 'bg-red-100'
                            }`}>
                              <Landmark className={`w-5 h-5 ${
                                payout.status === 'paid' ? 'text-emerald-600' : 
                                payout.status === 'pending' || payout.status === 'in_transit' ? 'text-blue-600' : 'text-red-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                ${payout.amount.toLocaleString()} Payout
                              </p>
                              <p className="text-sm text-slate-500">
                                {payout.arrival_date 
                                  ? `Arrives ${format(new Date(payout.arrival_date * 1000), "MMM d, yyyy")}` 
                                  : format(new Date(payout.created * 1000), "MMM d, yyyy")}
                              </p>
                            </div>
                          </div>
                          <Badge className={`rounded-lg capitalize ${
                            payout.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            payout.status === 'pending' || payout.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {payout.status === 'in_transit' ? 'In Transit' : payout.status}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Registrations Tab (existing internal data) */}
              <TabsContent value="registrations" className="mt-6">
                {/* Internal Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Registration Revenue</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          ${stats.total_revenue?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {stats.paid_count || 0} paid registrations
                    </p>
                  </div>

                  <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Pending</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          ${stats.pending_amount?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {stats.pending_count || 0} awaiting payment
                    </p>
                  </div>

                  <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Refunded</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          ${stats.refunded_amount?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 text-red-600" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {stats.refunded_count || 0} refunds processed
                    </p>
                  </div>
                </div>

                {/* Filters and Export */}
                <div className="bg-white border border-slate-200/60 rounded-2xl p-4 mb-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by player or program..."
                        aria-label="Search payments"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 rounded-xl"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger aria-label="Payment status" className="w-[180px] rounded-xl">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="refunded">Refunded</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={exportToCSV} className="rounded-xl">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* Payments Table */}
                <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Player</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Program</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredPayments.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                              No registration payments found
                            </td>
                          </tr>
                        ) : (
                          filteredPayments.map((payment, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {payment.created_at ? format(new Date(payment.created_at), "MMM d, yyyy") : "-"}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-slate-900">
                                {payment.player_name || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {payment.program_name || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-slate-900">
                                ${payment.amount?.toLocaleString() || 0}
                              </td>
                              <td className="px-4 py-3">
                                <Badge className={`rounded-lg capitalize ${
                                  payment.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                  payment.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {payment.payment_status || 'unknown'}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Promo Codes Tab */}
              <TabsContent value="discounts" className="mt-6">
                <PromoCodesTab token={token} isSuperAdmin={false} />
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="billing" className="mt-6">
                <div className="mb-4 rounded-2xl border border-[#d8ded9] bg-[#f6f8f4] p-4">
                  <p className="font-semibold text-[#101a19]">Sporthouse Billing</p>
                  <p className="mt-1 text-sm text-[#5f6b68]">Platform invoices and fees owed by your organization. Family invoices live in Family Receivables.</p>
                </div>
                <InvoicesTab token={token} />
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Fallback view when Stripe is not connected - show basic stats */}
        {(!stripeData || !stripeData.connected || !stripeData.active) && (
          <>
            <Tabs value={activeTab} onValueChange={handleOrgViewChange} className="w-full">
              <TabsList className="finance-tabs financial-workspace-tabs bg-slate-100 p-1">
                <TabsTrigger value="transactions" className="rounded-lg">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Ledger
                </TabsTrigger>
                <TabsTrigger value="collections" className="rounded-lg">
                  <Clock className="w-4 h-4 mr-2" />
                  Collections
                </TabsTrigger>
                <TabsTrigger value="receivables" className="rounded-lg">
                  <FileText className="w-4 h-4 mr-2" />
                  Family Receivables
                </TabsTrigger>
                <TabsTrigger value="discounts" className="rounded-lg">
                  <Ticket className="w-4 h-4 mr-2" />
                  Discounts
                </TabsTrigger>
                <TabsTrigger value="billing" className="rounded-lg">
                  <Receipt className="w-4 h-4 mr-2" />
                  Sporthouse Billing
                </TabsTrigger>
              </TabsList>

              <TabsContent value="transactions" className="mt-6 space-y-6">
            {/* Basic Stats Cards */}
            <section aria-labelledby="financial-snapshot-heading" className="space-y-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#087f67]">At a glance</p>
                <h2 id="financial-snapshot-heading" className="text-xl font-semibold tracking-tight text-[#101a19]">Financial snapshot</h2>
              </div>
              <div className="finance-metric-grid grid grid-cols-1 md:grid-cols-3">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Revenue</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      ${stats.total_revenue?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-7 h-7 text-emerald-600" />
                  </div>
                </div>
                <p className="text-sm text-emerald-600 mt-3 flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {stats.paid_count || 0} paid registrations
                </p>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      ${stats.pending_amount?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-amber-600" />
                  </div>
                </div>
                <p className="text-sm text-amber-600 mt-3 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {stats.pending_count || 0} awaiting payment
                </p>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Refunded</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      ${stats.refunded_amount?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                    <RefreshCw className="w-7 h-7 text-red-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3 flex items-center gap-1">
                  {stats.refunded_count || 0} refunds processed
                </p>
              </div>
              </div>
            </section>

            {/* Filters and Export */}
            <div className="platform-card operations-toolbar p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by player, program, or household..."
                    aria-label="Search payments"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger aria-label="Payment status" className="w-[180px] rounded-xl">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={exportToCSV} className="rounded-xl">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Payments Table */}
            <div className="platform-card operations-ledger overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Player</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Program</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                          No payments found
                        </td>
                      </tr>
                    ) : (
                      filteredPayments.map((payment, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {payment.created_at ? format(new Date(payment.created_at), "MMM d, yyyy") : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">
                            {payment.player_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {payment.program_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">
                            ${payment.amount?.toLocaleString() || 0}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`rounded-lg capitalize ${
                              payment.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                              payment.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {payment.payment_status || 'unknown'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
              </TabsContent>

              <TabsContent value="collections" className="mt-6">
                <CollectionsWorkspace token={token} peopleRouteBase={peopleRouteBase} />
              </TabsContent>

              {/* Promo Codes Tab for fallback view */}
              <TabsContent value="receivables" className="mt-6">
                <AdminOrgInvoices embedded />
              </TabsContent>

              <TabsContent value="discounts" className="mt-6">
                <PromoCodesTab token={token} isSuperAdmin={false} />
              </TabsContent>

              {/* Invoices Tab for fallback view */}
              <TabsContent value="billing" className="mt-6">
                <div className="mb-4 rounded-2xl border border-[#d8ded9] bg-[#f6f8f4] p-4">
                  <p className="font-semibold text-[#101a19]">Sporthouse Billing</p>
                  <p className="mt-1 text-sm text-[#5f6b68]">Platform invoices and fees owed by your organization. Family invoices live in Family Receivables.</p>
                </div>
                <InvoicesTab token={token} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminFinancials;
